import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Card, CardContent, Typography, Chip, TablePagination } from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import { TableToolbar, ColumnConfig } from '../../components/TableToolbar';
import axiosInstance from '../../lib/axios';
import {
  CreditAccount,
  CreditAccountStatus,
  PaginatedCreditAccountsResponse,
} from '../../types/credit';

interface FetchCreditAccountsResult {
  data: CreditAccount[];
  total: number;
}

const fetchCreditAccounts = async (
  page: number,
  pageSize: number
): Promise<FetchCreditAccountsResult> => {
  const response = await axiosInstance.get<PaginatedCreditAccountsResponse>('/credit/accounts', {
    params: {
      page: page + 1,
      limit: pageSize,
    },
  });

  return {
    data: response.data.data,
    total: response.data.pagination.total,
  };
};

const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const getCustomerLabel = (
  customer?: { firstName?: string; lastName?: string; email?: string } | null
) => {
  if (!customer) return '—';
  const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  return name || customer.email || '—';
};

const getCustomerContact = (customer?: { email?: string; phone?: string } | null) => {
  if (!customer) return '—';
  return customer.phone || customer.email || '—';
};

const STATUS_LABEL_KEYS: Record<CreditAccountStatus, string> = {
  [CreditAccountStatus.ACTIVE]: 'credit.status.active',
  [CreditAccountStatus.SUSPENDED]: 'credit.status.suspended',
  [CreditAccountStatus.CLOSED]: 'credit.status.closed',
};

const STATUS_COLORS: Record<CreditAccountStatus, 'success' | 'warning' | 'default'> = {
  [CreditAccountStatus.ACTIVE]: 'success',
  [CreditAccountStatus.SUSPENDED]: 'warning',
  [CreditAccountStatus.CLOSED]: 'default',
};

interface CreditListProps {
  onAccountClick?: (account: CreditAccount) => void;
}

export const CreditList = ({ onAccountClick }: CreditListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['credit-accounts', page, pageSize],
    queryFn: () => fetchCreditAccounts(page, pageSize),
  });

  const rows = useMemo(() => data?.data || [], [data?.data]);

  const getStatusLabel = (status: CreditAccountStatus) => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? t(key) : '—';
  };

  const getExportColumns = (): ColumnConfig[] => [
    {
      field: 'customer',
      headerName: t('credit.list.customer'),
      valueFormatter: (_value, row) => getCustomerLabel(row.customer),
    },
    {
      field: 'contact',
      headerName: t('credit.list.contact'),
      valueFormatter: (_value, row) => getCustomerContact(row.customer),
    },
    { field: 'balance', headerName: t('credit.balance'), valueFormatter: v => formatCurrency(v) },
    {
      field: 'creditLimit',
      headerName: t('credit.creditLimit'),
      valueFormatter: v => formatCurrency(v),
    },
    {
      field: 'status',
      headerName: t('credit.status.label'),
      valueFormatter: v => getStatusLabel(v),
    },
  ];

  const handleRowClick = (account: CreditAccount) => {
    if (onAccountClick) {
      onAccountClick(account);
    } else {
      navigate(`/app/credit/${account.id}`);
    }
  };

  const tableColumns: Column<CreditAccount>[] = [
    {
      field: 'customer',
      headerName: t('credit.list.customer'),
      render: (_value, row) => (
        <Typography variant="body2" fontWeight={600}>
          {getCustomerLabel(row.customer)}
        </Typography>
      ),
    },
    {
      field: 'contact',
      headerName: t('credit.list.contact'),
      render: (_value, row) => (
        <Typography variant="body2" color="text.secondary">
          {getCustomerContact(row.customer)}
        </Typography>
      ),
    },
    {
      field: 'balance',
      headerName: t('credit.balance'),
      render: value => (
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(value)}
        </Typography>
      ),
    },
    {
      field: 'creditLimit',
      headerName: t('credit.creditLimit'),
      render: value => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'status',
      headerName: t('credit.status.label'),
      render: value => (
        <Chip
          label={getStatusLabel(value)}
          size="small"
          color={STATUS_COLORS[value as CreditAccountStatus] || 'default'}
          variant={value === CreditAccountStatus.ACTIVE ? 'filled' : 'outlined'}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">{t('credit.list.title')}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('credit.list.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TableToolbar
            title={t('credit.list.title')}
            searchValue=""
            onSearchChange={() => {}}
            hideSearch
            data={rows}
            columns={getExportColumns()}
            exportFileName="credit-accounts"
          />

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('credit.list.error')}
            </Alert>
          )}

          <ResponsiveTable
            columns={tableColumns}
            rows={rows}
            loading={isLoading}
            emptyMessage={t('credit.list.empty')}
            primaryField="customer"
            getPrimaryText={row => getCustomerLabel(row.customer)}
            getSecondaryText={row => formatCurrency(row.balance)}
            onRowClick={handleRowClick}
          />
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreditList;
